/**
 * AppSidebar — Precision Compliance Group
 *
 * Modern, compact rail + clean app launcher panel.
 * PSI (Precision Secured Intelligence — formerly POM) is the compliance
 * command center; it is hidden from department-bound users. They land on the
 * Workspace welcome screen instead.
 * Permission logic: see canSeeApp() — fail-closed against unknown user types.
 */

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { canSeeApp as sharedCanSeeApp } from '../lib/canSeeApp';
import {
  ViewfinderCircleIcon,
  InboxIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentListIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  FlagIcon,
  MagnifyingGlassIcon,
  GlobeAltIcon,
  BookOpenIcon,
  PaperAirplaneIcon,
  BuildingOffice2Icon,
  ScaleIcon,
  ChartBarSquareIcon,
  ArrowsRightLeftIcon,
  ShieldExclamationIcon,
  CpuChipIcon,
  PresentationChartBarIcon,
} from '@heroicons/react/24/outline';

// ---------------------------------------------------------------------------
// App registry
// ---------------------------------------------------------------------------
const APP_META = {
  overmind:         { Icon: ViewfinderCircleIcon,         label: 'Precision Secured Intelligence', desc: 'Command center for compliance intelligence', color: '#7c3aed' },
  workstream:       { Icon: InboxIcon,                    label: 'Workstream',        desc: 'Cross-module AI follow-ups',     color: '#06b6d4' },
  calendar:         { Icon: CalendarDaysIcon,             label: 'Compliance Hub',   desc: 'Obligations & calendar',        color: '#0ea5e9' },
  risk:             { Icon: ExclamationTriangleIcon,      label: 'Risk Assessment',  desc: 'Risk scoring & register',       color: '#f59e0b' },
  audit:            { Icon: ClipboardDocumentListIcon,    label: 'Audit',             desc: 'CMS and internal audits, end to end', color: '#0d9488' },
  coa:              { Icon: ClipboardDocumentListIcon,    label: 'COA Work Plan',     desc: 'Audit & monitoring oversight',  color: '#8b5cf6' },
  cil:              { Icon: PencilSquareIcon,             label: 'Issues Log',        desc: 'Compliance issues tracking',    color: '#f97316' },
  privacy:          { Icon: ShieldCheckIcon,              label: 'Precision Privacy', desc: 'Privacy program and incidents', color: '#6366f1' },
  memo:             { Icon: DocumentTextIcon,             label: 'Mandate',           desc: 'Regulatory memo analysis',      color: '#10b981' },
  mandate_native:   { Icon: DocumentTextIcon,             label: 'Precision Mandate', desc: 'Regulatory command center',     color: '#F58220' },
  cap:              { Icon: FlagIcon,                     label: 'CAP',               desc: 'Corrective action plans',       color: '#eab308' },
  scrubber:         { Icon: MagnifyingGlassIcon,          label: 'Scrubber',          desc: 'Universe validation, current and 2027 audit year protocols', color: '#14b8a6' },
  scrubber_2029:    { Icon: MagnifyingGlassIcon,          label: 'Scrubber',          desc: 'Universe validation, 2027 audit year protocols', color: '#14b8a6' },
  network_adequacy: { Icon: GlobeAltIcon,                 label: 'Network Adequacy', desc: 'Provider network analysis',      color: '#06b6d4' },
  ppm:              { Icon: BookOpenIcon,                 label: 'Policy Management', desc: 'Enterprise policy system',       color: '#818cf8' },
  ratify:           { Icon: PaperAirplaneIcon,            label: 'SignoffHub',        desc: 'Committee · CEO · board sign-off', color: '#4f46e5' },
  fdr:              { Icon: BuildingOffice2Icon,          label: 'FDR Oversight',     desc: 'Vendor & delegate oversight',    color: '#f43f5e' },
  priq:             { Icon: ScaleIcon,                    label: 'RuleIQ',            desc: 'CMS Final Rule intelligence',   color: '#f59e0b' },
  reporter:         { Icon: ChartBarSquareIcon,           label: 'Reporter',          desc: 'Part C/D Reporting validator',  color: '#c8323f' },
  universe_requests:{ Icon: ArrowsRightLeftIcon,          label: 'Universe Requests', desc: 'Request universes from FDRs',   color: '#c8323f' },
  sentinel:         { Icon: ShieldExclamationIcon,        label: 'Sentinel',          desc: 'Fraud (FWA) investigations',    color: '#dc2626' },
  ai_command:       { Icon: CpuChipIcon,                  label: 'Precision Responsible AI', desc: 'Responsible AI governance',     color: '#E5601F' },
  deck_studio:      { Icon: PresentationChartBarIcon,     label: 'Deck Studio',       desc: 'Org-branded PowerPoint builder', color: '#1f4a3f' },
  materials_review: { Icon: DocumentTextIcon,             label: 'Materials Review',  desc: 'Marketing and communications materials', color: '#E5601F' },
  readiness:        { Icon: ClipboardDocumentListIcon,    label: 'Readiness',         desc: 'CMS annual readiness checklist', color: '#E5601F' },
  agent:            { Icon: CpuChipIcon,                  label: 'Agent',             desc: 'The Compliance Agent, what it watches', color: '#E5601F' },
};

const PANEL_CATEGORIES = [
  { label: 'Risk Module',                 apps: ['risk', 'cil'] },
  { label: 'Audit & Monitoring',          apps: ['audit', 'coa'] },
  { label: 'Privacy',                     apps: ['privacy'] },
  { label: 'FDR',                         apps: ['fdr'] },
  { label: 'FWA',                         apps: ['sentinel'] },
  { label: 'Responsible AI',               apps: ['ai_command'] },
  { label: 'Materials',                   apps: ['materials_review'] },
  { label: 'Policies',                    apps: ['ppm'] },
  // 'memo' (standalone Mandate) removed 2026-08-12 — app shut down, tile led
  // to a dead host. Precision Mandate ('mandate_native') is the replacement.
  { label: 'Tools',                       apps: ['agent', 'readiness', 'deck_studio', 'workstream', 'calendar', 'ratify', 'mandate_native', 'cap', 'network_adequacy', 'scrubber', 'priq', 'reporter', 'universe_requests'] },
];

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------
export default function AppSidebar({ selectedApp, onSelectApp, user }) {
  const [appsOpen, setAppsOpen] = useState(false);
  const panelRef  = useRef(null);
  const triggerRef = useRef(null);

  // Visibility gate lives in src/lib/canSeeApp.js — single source of truth
  // for BOTH the left rail panel and OracleHome's PSI top-right launcher.
  // Operators saw the same user get different app lists in the two launchers;
  // the two gates have been collapsed to one to make that impossible.
  const canSeeApp = (key) => sharedCanSeeApp(key, user);

  // Close panel on outside click
  useEffect(() => {
    if (!appsOpen) return;
    const handler = (e) => {
      if (
        panelRef.current  && !panelRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) setAppsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [appsOpen]);

  const handleSelect = (key) => { onSelectApp(key); setAppsOpen(false); };

  const initials   = user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'U';
  const firstName  = user?.name?.split(' ')[0] || '';
  // Apps "behind" the launcher are everything that isn't the user's home tile
  // — PSI for compliance, Workspace for dept users. Used to highlight the
  // launcher icon when the user is inside any of those panel apps.
  const inPanelApp = selectedApp && selectedApp !== 'overmind' && selectedApp !== 'workspace';

  return (
    <>
      {/* ═══ RAIL — 64px, always visible ═══ */}
      <div className="sticky top-0 self-start flex-shrink-0 w-16 h-screen flex flex-col items-center py-4 bg-slate-900/95 backdrop-blur-xl border-r border-slate-700/50 z-40">

        {/* Brand — the PCG lighthouse (the platform vendor's mark, not the
            client's; the client logo lives in the top bar). White tower on
            this dark rail, brand-orange signal, same as every dark surface. */}
        <div className="mb-4 flex-shrink-0">
          <svg className="w-9 h-9" viewBox="0 0 512 512" role="img" aria-label="Precision Compliance Group">
            <defs>
              <linearGradient id="railPcgLight" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#F79A3E" />
                <stop offset="1" stopColor="#E5601F" />
              </linearGradient>
            </defs>
            <g fill="none" stroke="url(#railPcgLight)" strokeWidth="26" strokeLinecap="round">
              <path d="M148 88 A 150 150 0 0 0 148 216" />
              <path d="M364 88 A 150 150 0 0 1 364 216" />
            </g>
            <circle cx="256" cy="152" r="46" fill="url(#railPcgLight)" />
            <path d="M212 232 h88 l40 214 a16 16 0 0 1 -16 20 H188 a16 16 0 0 1 -16 -20 Z" fill="#FFFFFF" />
          </svg>
        </div>

        {/* PSI — compliance command center. Rendered only for users who
            can actually see PSI; matches the canSeeApp gate above. Dept users
            see the Workspace tile instead. */}
        {user && canSeeApp('overmind') && (
          <button
            onClick={() => handleSelect('overmind')}
            className={`relative w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 flex-shrink-0 group
              ${selectedApp === 'overmind'
                ? 'bg-gradient-to-br from-orange-500/20 to-amber-600/20 ring-1 ring-orange-500/30 shadow-lg shadow-orange-500/10'
                : 'hover:bg-slate-700/50'}`}
            title="Precision Secured Intelligence"
          >
            {selectedApp === 'overmind' && (
              <div className="absolute -left-[9px] w-[3px] h-5 bg-gradient-to-b from-orange-400 to-amber-400 rounded-r-full" />
            )}
            <ViewfinderCircleIcon className={`w-5 h-5 ${selectedApp === 'overmind' ? 'text-white' : 'text-slate-400 group-hover:text-slate-200'}`} strokeWidth={1.75} />
          </button>
        )}

        {/* Workspace — dept users' home. Only rendered for users who cannot
            see PSI (i.e. department-bound). Compliance users have no need
            for this tile since their default home is PSI itself. */}
        {user && !canSeeApp('overmind') && (
          <button
            onClick={() => handleSelect('workspace')}
            className={`relative w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 flex-shrink-0 group
              ${selectedApp === 'workspace'
                ? 'bg-gradient-to-br from-orange-500/20 to-amber-600/20 ring-1 ring-orange-500/30 shadow-lg shadow-orange-500/10'
                : 'hover:bg-slate-700/50'}`}
            title="Home"
          >
            {selectedApp === 'workspace' && (
              <div className="absolute -left-[9px] w-[3px] h-5 bg-gradient-to-b from-orange-400 to-amber-400 rounded-r-full" />
            )}
            <svg className={`w-4 h-4 transition-colors ${selectedApp === 'workspace' ? 'text-white' : 'text-slate-400 group-hover:text-slate-200'}`} fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
            </svg>
          </button>
        )}

        <div className="w-6 h-px bg-slate-700/50 my-3 flex-shrink-0" />

        {/* Apps launcher */}
        <button
          ref={triggerRef}
          onClick={() => setAppsOpen(v => !v)}
          className={`relative w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 flex-shrink-0 group
            ${appsOpen
              ? 'bg-gradient-to-br from-orange-500/20 to-amber-600/20 ring-1 ring-orange-500/30 shadow-lg shadow-orange-500/10'
              : inPanelApp
                ? 'bg-slate-700/50 ring-1 ring-slate-600/50'
                : 'hover:bg-slate-700/50'}`}
          title="Apps"
        >
          {inPanelApp && !appsOpen && (
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-blue-400 ring-2 ring-slate-900" />
          )}
          {inPanelApp && (
            <div className="absolute -left-[9px] w-[3px] h-5 bg-gradient-to-b from-orange-400 to-amber-400 rounded-r-full" />
          )}
          <svg className={`w-4 h-4 transition-colors ${appsOpen || inPanelApp ? 'text-white' : 'text-slate-400 group-hover:text-slate-200'}`} fill="currentColor" viewBox="0 0 16 16">
            <rect x="1" y="1" width="6" height="6" rx="1.5"/><rect x="9" y="1" width="6" height="6" rx="1.5"/>
            <rect x="1" y="9" width="6" height="6" rx="1.5"/><rect x="9" y="9" width="6" height="6" rx="1.5"/>
          </svg>
        </button>

        <div className="flex-1" />

        {/* People (Stage 5 — new user management page) — visible to any
            org-admin tier user. Compliance Officer was added 2026-05-17 so
            they can onboard their own team without the Org Superadmin being
            a bottleneck. Mirrors server's requireOrgManageAccess. */}
        {(() => {
          const ut = user?.user_type || user?.effective_user_type || null;
          const canManage =
            ut === 'platform_superuser' ||
            ut === 'org_superadmin' ||
            ut === 'compliance_officer' ||
            user?.role === 'superadmin' ||
            user?.role === 'superuser' ||
            (!ut && user?.role === 'admin' && !user?.department_id);
          return canManage;
        })() && (
          <Link
            to="/people"
            className="w-10 h-10 rounded-xl flex items-center justify-center mb-2 hover:bg-slate-700/50 transition-all duration-200 group flex-shrink-0"
            title="People (manage users)"
          >
            <svg className="w-4 h-4 text-slate-400 group-hover:text-slate-200 transition-colors" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"/>
            </svg>
          </Link>
        )}

        {/* Organization setup — reopen the onboarding wizard any time to
            confirm departments, aliases, contracts, and team. Same org-admin
            tier gate as People (server's requireOrgManageAccess is authoritative).
            An amber dot flags an org whose first-time setup is not yet complete. */}
        {(() => {
          const ut = user?.user_type || user?.effective_user_type || null;
          return (
            ut === 'platform_superuser' ||
            ut === 'org_superadmin' ||
            ut === 'compliance_officer' ||
            user?.role === 'superadmin' ||
            user?.role === 'superuser' ||
            (!ut && user?.role === 'admin' && !user?.department_id)
          );
        })() && (
          <Link
            to="/onboarding"
            className="relative w-10 h-10 rounded-xl flex items-center justify-center mb-2 hover:bg-slate-700/50 transition-all duration-200 group flex-shrink-0"
            title="Organization setup (departments, aliases, team)"
          >
            <svg className="w-4 h-4 text-slate-400 group-hover:text-slate-200 transition-colors" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {user?.organization_setup_complete === false && (
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-400 ring-2 ring-slate-900" />
            )}
          </Link>
        )}

        {/* Team & Access — compliance officer's per-app grant manager. Visible
            to org admins (compliance officer, no department) and to platform-
            level roles. Routes /api/admin/team enforce auth server-side too. */}
        {(() => {
          const ut = user?.user_type || user?.effective_user_type || null;
          return (
            ut === 'platform_superuser' ||
            ut === 'org_superadmin' ||
            ut === 'compliance_officer' ||
            user?.role === 'superadmin' ||
            user?.role === 'superuser' ||
            (!ut && user?.role === 'admin' && !user?.department_id)
          );
        })() && (
          <Link
            to="/team-access"
            className="w-10 h-10 rounded-xl flex items-center justify-center mb-2 hover:bg-slate-700/50 transition-all duration-200 group flex-shrink-0"
            title="Team & Access — invite teammates, grant per-app access, observers"
          >
            <svg className="w-4 h-4 text-slate-400 group-hover:text-slate-200 transition-colors" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"/>
            </svg>
          </Link>
        )}

        {/* Legacy Settings — kept for Contracts, PBPs, Department Mapping, and Bulk CSV imports.
            All other functionality (user management, org provisioning, departments) has moved
            to /people and /platform-admin. */}
        {['superadmin', 'superuser'].includes(user?.role) && (
          <Link
            to="/settings"
            className="w-10 h-10 rounded-xl flex items-center justify-center mb-2 hover:bg-slate-700/50 transition-all duration-200 group flex-shrink-0 opacity-60 hover:opacity-100"
            title="Legacy: Contracts, PBPs, Mapping, CSV"
          >
            <svg className="w-4 h-4 text-slate-400 group-hover:text-slate-200 transition-colors" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z"/>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
          </Link>
        )}

        {/* User */}
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center border border-blue-400/30 shadow-lg shadow-blue-500/15 flex-shrink-0">
          <span className="text-[10px] font-bold text-white">{initials}</span>
        </div>
      </div>

      {/* ═══ APPS PANEL — slide-in ═══ */}
      {appsOpen && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setAppsOpen(false)} />
          <div
            ref={panelRef}
            className="fixed left-16 top-0 h-screen w-80 z-40 flex flex-col bg-slate-800/95 backdrop-blur-2xl border-r border-slate-700/50 shadow-2xl"
            style={{ animation: 'sideIn .18s cubic-bezier(.16,1,.3,1)' }}
          >
            <style>{`@keyframes sideIn { from { opacity:0; transform:translateX(-12px) } to { opacity:1; transform:translateX(0) } }`}</style>

            {/* Header */}
            <div className="px-5 pt-5 pb-4 border-b border-slate-700/50 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-[14px] font-semibold text-white">{firstName ? `Hi, ${firstName}` : 'Apps'}</h2>
                  <p className="text-[11px] text-slate-500 mt-0.5">Switch workspace</p>
                </div>
                <button
                  onClick={() => setAppsOpen(false)}
                  className="w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700/60 transition-all duration-200"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
              </div>
            </div>

            {/* App list by category — a grouped list (icon chip + label + desc)
                rather than a grid, so single-app categories read cleanly. */}
            <div className="flex-1 overflow-y-auto py-3 px-2.5 space-y-5
              [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-thumb]:bg-slate-600 [&::-webkit-scrollbar-thumb]:rounded-full">

              {PANEL_CATEGORIES.map(cat => {
                const visible = cat.apps.filter(canSeeApp);
                if (!visible.length) return null;
                return (
                  <div key={cat.label}>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500 px-2.5 mb-1.5">
                      {cat.label}
                    </p>
                    <div className="space-y-0.5">
                      {visible.map(key => {
                        const meta = APP_META[key] || { Icon: null, label: key, desc: '', color: '#64748b' };
                        const active = selectedApp === key;
                        // External apps render as anchors so the browser handles
                        // top-level navigation natively (see OracleHome's launcher).
                        const extMap = { memo: 'mandate', knowledge: 'knowledge' };
                        const externalSub = extMap[key];
                        let externalHref = externalSub ? (() => {
                          const isAzure = typeof window !== 'undefined' && window.location.hostname.startsWith('az-');
                          return `https://${isAzure ? `az-${externalSub}` : externalSub}.precisioncompliancegroup.com`;
                        })() : null;
                        const rowClassName = `group flex items-center gap-3 w-full text-left px-2 py-2 rounded-xl transition-all duration-150
                              ${active
                                ? 'bg-gradient-to-r from-orange-500/15 to-amber-600/15 ring-1 ring-orange-500/25'
                                : 'hover:bg-slate-700/40'}`;
                        const rowInner = (
                          <>
                            <div
                              className="relative w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: meta.color + '20' }}
                            >
                              {meta.Icon && <meta.Icon className="w-[18px] h-[18px]" style={{ color: meta.color }} strokeWidth={1.75} />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className={`text-[13px] font-medium leading-tight truncate ${active ? 'text-white' : 'text-slate-200 group-hover:text-white'}`}>
                                {meta.label}
                              </p>
                              {meta.desc && (
                                <p className="text-[11px] leading-tight truncate text-slate-500">{meta.desc}</p>
                              )}
                            </div>
                            {active && <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />}
                          </>
                        );
                        if (externalHref) {
                          if (externalHref.startsWith('/')) {
                            return (
                              <Link key={key} to={externalHref} onClick={() => setAppsOpen(false)} className={rowClassName}>
                                {rowInner}
                              </Link>
                            );
                          }
                          return (
                            <a key={key} href={externalHref} className={rowClassName}>
                              {rowInner}
                            </a>
                          );
                        }
                        return (
                          <button key={key} onClick={() => handleSelect(key)} className={rowClassName}>
                            {rowInner}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="border-t border-slate-700/50 px-4 py-3 flex items-center gap-2.5 flex-shrink-0">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center border border-blue-400/30 flex-shrink-0">
                <span className="text-[9px] font-bold text-white">{initials}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-slate-300 truncate">{user?.name || 'User'}</p>
                <p className="text-[10px] text-slate-500 capitalize">{user?.role || 'user'}</p>
              </div>
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" title="Online" />
            </div>
          </div>
        </>
      )}
    </>
  );
}
